import {
  flatIcon,
  bullishIcon,
  bearishIcon,
  choppyIcon,
  choppyBlueIcon,
  bullishBlueIcon,
  bearishBlueIcon,
  flatBlueIcon,
  iconSettings,
} from "assets/icons";

import { BM_SERIES_KEY, FEAR_GREED_COLORS } from "utils/constants";
import {
  COLOR_GREEN,
  COLOR_RED,
  COLOR_STRATEGY_BLUE,
  COLOR_STRATEGY_GREY,
} from "style/theme";
import { format, MONTH_DATE_YEAR_FORMAT } from "utils/date";
import {
  contains,
  getRandomColor,
  roundOff,
  numberSeparator,
  getChartColor,
  isEmptyObj,
} from "utils";

const getFearGreedColorSegments = (data, key) => {
  const segments = [];
  const dataLen = data.length;

  for (let i = 0; i < dataLen; i++) {
    const d = data[i];
    const value = d[key];
    const percentage = 100 - ((dataLen - i - 1) / (dataLen - 1)) * 100;

    if (value > 80) {
      segments.push({ color: FEAR_GREED_COLORS[80], percentage });
      // segments.push({
      //   color: FEAR_GREED_COLORS[80],
      //   percentage: percentage + 1,
      // });
    } else if (value > 60) {
      segments.push({ color: FEAR_GREED_COLORS[60], percentage });
      segments.push({
        color: FEAR_GREED_COLORS[60],
        percentage: percentage - 1,
      });
    } else if (value > 40) {
      segments.push({
        color: FEAR_GREED_COLORS[40],
        percentage: percentage + 1,
      });
      segments.push({ color: FEAR_GREED_COLORS[40], percentage });
    } else if (value > 20) {
      segments.push({ color: FEAR_GREED_COLORS[20], percentage });
      segments.push({
        color: FEAR_GREED_COLORS[20],
        percentage: percentage - 1,
      });
    } else {
      segments.push({ color: FEAR_GREED_COLORS[0], percentage });
      segments.push({
        color: FEAR_GREED_COLORS[0],
        percentage: percentage + 1,
      });
    }
  }
  return segments;
};

const generatesDatesBetween = (startDate, endDate, counts) => {
  const currentDate = startDate;
  const dates = [];
  for (let ind = 0; ind < 365; ind++) {
    let currDate = format(MONTH_DATE_YEAR_FORMAT, new Date(currentDate));
    if (counts?.length > ind) {
      dates.push({ date: currDate, count: counts[ind].pl });
    } else {
      dates.push({ date: currDate, count: 0 });
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

export const calcGainPercentage = (firstValue, value) => {
  if (!firstValue || !value) {
    return 0;
  }
  let calcGainPer = value / firstValue;
  calcGainPer = (calcGainPer - 1) * 100;
  return calcGainPer;
};

export const getHpLineColors = (data, mini) => {
  const id = Math.random();

  if (!data?.length) {
    return [{ id, key: "", color: "" }];
  }

  let keys = [];
  const firstIndex = data[0];
  const lastIndex = data[data.length - 1];

  if (data.length > 0) {
    keys = Object.keys(lastIndex);
  }
  if (data.length > 0 && "y" in data[0]) {
    keys.push("y");
  }

  return (keys || [])
    .filter(
      (item) =>
        item !== "name" &&
        item !== "dollarValue" &&
        item !== "perValue" &&
        item !== "hideTimestamp"
    )
    .map((key) => {
      let color = getRandomColor();
      let segments = [];

      if (contains(key, "Strategy") || contains(key, "Profit")) {
        color = COLOR_STRATEGY_BLUE; // Use strategy blue for strategy lines
        if (lastIndex[key] < firstIndex[key]) {
          color = COLOR_RED; // red for negative performance
        }
        // segments = getColorSegments(data, key);
      } else if (contains(key, "Benchmark")) {
        color = COLOR_STRATEGY_GREY; // Use strategy grey for benchmark lines
        // segments = [{ color, percentage: 100 }];
      } else if (mini && key === "$") {
        color = COLOR_GREEN;
        if (lastIndex["$"] < 10000) {
          color = COLOR_RED;
        }
        // segments = [{ color, percentage: 100 }];
      }
      return {
        id,
        key,
        color,
        segments,
      };
    });
};

export const getMetricsLineColors = (data, mini) => {
  const id = Math.random();

  if (!data?.length) {
    return [{ id, key: "", color: "" }];
  }

  let keys = [];
  const firstIndex = data[0];
  const lastIndex = data[data.length - 1];

  if (data.length > 0) {
    keys = Object.keys(lastIndex);
  }
  if (data.length > 0 && "y" in data[0]) {
    keys.push("y");
  }

  return (keys || [])
    .filter(
      (item) =>
        item !== "name" &&
        item !== "series" &&
        item !== "perValue" &&
        item !== "hideTimestamp"
    )
    .map((key) => {
      let color = getRandomColor();
      let segments = [];

      if (
        contains(key, "Subscribers") ||
        contains(key, "Profit") ||
        contains(key, "Total Aum")
      ) {
        color = COLOR_STRATEGY_BLUE; // Use strategy blue for metrics lines
        if (lastIndex[key] < firstIndex[key]) {
          color = COLOR_RED; // red for negative performance
        }
        // segments = getColorSegments(data, key);
      } else if (mini && key === "$") {
        color = COLOR_GREEN;
        if (lastIndex["$"] < 10000) {
          color = COLOR_RED;
        }
        // segments = [{ color, percentage: 100 }];
      }
      return {
        id,
        key,
        color,
        segments,
      };
    });
};

export const getSectorLineColors = (data) => {
  const id = Math.random();

  if (!data?.length) {
    return [{ id, key: "", color: "" }];
  }

  let keys = [];

  if (data.length > 0) {
    if ("y" in data[0]) {
      keys.push("y");
    }
    if ("value" in data[0]) {
      keys.push("value");
    }
  }

  return (keys || []).map((key) => {
    let color = getRandomColor();
    let segments;

    if (contains(key, "y")) {
      color = COLOR_GREEN;
      segments = getFearGreedColorSegments(data, key);
    } else if (contains(key, "value")) {
      color = COLOR_GREEN;
    }

    return {
      id,
      key,
      color,
      segments,
    };
  });
};

export const getHpFilteredData = (data = []) => {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    const hp = data[i];
    let filtered = { dollarValue: true };
    for (const key in hp) {
      if (
        contains(key, "name") ||
        contains(key, "Benchmark") ||
        contains(key, "Strategy")
      ) {
        filtered[key] = hp[key];
      }
    }
    result.push(filtered);
  }
  return result;
};

export const getMetricsFilteredData = (data = []) => {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    const hp = data[i];
    let filtered = { series: true };
    for (const key in hp) {
      if (
        contains(key, "name") ||
        contains(key, "Subscribers") ||
        contains(key, "Total Aum")
      ) {
        filtered[key] = hp[key];
      }
    }
    result.push(filtered);
  }
  return result;
};

export const getHpFilteredDataWithTimeframe = (data) => {
  let output = {};
  for (let i in data) {
    output[data[i].key] = getHpFilteredData(data[i].value);
  }
  return output;
};

// remove a specific series key from each point in an array
export const stripKeyFromPoints = (points = [], key) => {
  if (!Array.isArray(points)) return [];
  return points.map((point) => {
    const rest = { ...point };
    delete rest[key];
    return rest;
  });
};

// remove benchmark series from historical performance data grouped by timeframe
// accepts an object mapping timeframe (e.g., "3M", "1Y") to arrays of points
// returns a new object with the benchmark series removed from each timeframe dataset
export const stripBenchmarkFromHpData = (dataByPeriod = {}) => {
  const output = {};
  for (const period of Object.keys(dataByPeriod || {})) {
    output[period] = stripKeyFromPoints(dataByPeriod[period], BM_SERIES_KEY);
  }
  return output;
};

export const getMetricsFilteredDataWithTimeframe = (data) => {
  let output = {};
  for (let i in data) {
    output[data[i].key] = getMetricsFilteredData(data[i].value);
  }
  return output;
};

export const getFilteredSectorAllocation = (data) => {
  const result = [];
  const _data = Object.keys(data);
  for (let i = 0; i < _data.length; i++) {
    const key = _data[i];
    result.push({
      label: key,
      value: data[key],
    });
  }
  return result.sort((a, b) => b.value - a.value);
};

export const getFilteredDrawdownData = (data = []) => {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    result.push({
      label: "Drawdown:",
      name: d.name,
      value: d.Drawdown * 100,
      perValue: true,
    });
  }
  return result;
};

export const prepareHeatmapData = (counts = {}) => {
  const currentYear = new Date().getFullYear();
  const preparedData = generatesDatesBetween(
    new Date(`${currentYear}-01-01T00:00:00`),
    new Date(`${currentYear}-12-31T00:00:00`),
    counts
  );
  return {
    start: `${currentYear - 1}-12-31`,
    end: `${currentYear}-12-31`,
    counts: preparedData,
  };
};

export const getAtVolumeFilteredDataExtended = (data = {}) => {
  const total = Object.keys(data).length;
  const sorted = Object.entries(data)
    .sort(
      ([, first], [, second]) =>
        second["Total # of Trades"] - first["Total # of Trades"]
    )
    .reduce((dataArray, [key, value], idx) => {
      const tileSizeFormula = total - idx * 0.5;
      const noOfTrades = value["Total # of Trades"] || 0;
      const winningTrades = value["Winning Trades"] || 0;
      const losingTrades = value["Losing Trades"] || 0;
      let profit = value["Profit"] || 0;
      profit = roundOff(profit);
      profit = numberSeparator(Number(profit));

      dataArray.push({
        name: key,
        size: noOfTrades,
        tileSize: tileSizeFormula,
        noOfTrades: `Total # of Trades: ${noOfTrades}`,
        winningTrades: `Winning Trades: ${winningTrades}`,
        losingTrades: `Losing Trades: ${losingTrades}`,
        profit: `Profit: $${profit}`,
      });
      return dataArray;
    }, []);
  return sorted;
};

export const getCalendarReturnsFilteredData = (data = {}) => {
  let filtered = [];

  const mappingData = isEmptyObj(data.calendar_years)
    ? data?.calendar_years_benchmark
    : data?.calendar_years;

  for (const key in mappingData) {
    filtered.push({
      name: key,
      label: "Strategy",
      label2: "S&P 500",
      value: data?.calendar_years[key] || 0,
      value2: data?.calendar_years_benchmark
        ? data?.calendar_years_benchmark[key]
        : 0,
      color: "rgba(4, 138, 245, 1)",
      color2: "rgba(120, 206, 238, 1)",
      hideTimestamp: true,
    });
  }
  return filtered;
};

export const fixedNumberToTwoDecimals = (val) => {
  const percVal = val * 100;
  return `${roundOff(Number(percVal), 0)}%`;
};

export const getPerformanceCellInfo = (value) => {
  let sign = "";
  let className = "";
  if (value < 0) {
    className = "loss";
  } else if (value > 0) {
    sign = "+";
    className = "gain";
  }
  return { className, sign };
};

export const getStatusCellClass = (value) => {
  let className = "";
  if (value == "Active") {
    className = "gain";
  } else if (value == "Stop") {
    className = "text-primary";
  }
  return className;
};

export const buttonTextHandler = (subscriptionStatus) => {
  if (subscriptionStatus === "Active") {
    return { id: "pause", title: "Pause Strategy" };
  } else {
    return {
      id: "start",
      title: "Start Strategy",
    };
  }
};

export const buttonTextHandlerV2 = (subscriptionStatus) => {
  if (subscriptionStatus === "Active") {
    return { id: "pause", title: "Pause strategy" };
  } else {
    return {
      id: "start",
      title: "Start strategy",
    };
  }
};

export const insertSelectedUnit = (value, unit) => {
  const fixedValue = roundOff(Number(value));
  return unit === "%" ? `${fixedValue}${unit}` : `${unit}${fixedValue}`;
};

export const renderScenarioIcon = (type, isActive) => {
  const iconMeta = {
    BULL: isActive ? bullishBlueIcon : bullishIcon,
    BEAR: isActive ? bearishBlueIcon : bearishIcon,
    CHOPPY: isActive ? choppyBlueIcon : choppyIcon,
    FLAT: isActive ? flatBlueIcon : flatIcon,
  };

  return iconMeta[type] ?? iconSettings;
};

export const prepareTopHoldingsData = (data) => {
  const usedColors = [];
  return (
    data?.map((data) => {
      const color = getChartColor(usedColors);
      usedColors.push(color);
      return { ...data, color, fill: color };
    }) || []
  );
};

export const getColoredData = (data) => {
  const usedColors = [];

  return (
    data?.map((item) => {
      let color;
      if (item.name.toLowerCase() === "positive") {
        color = "#2BC155";
      } else if (item.name.toLowerCase() === "neutral") {
        color = "#A5C7FF40";
      } else if (item.name.toLowerCase() === "negative") {
        color = "#FF0000";
      } else {
        color = getChartColor(usedColors);
        usedColors.push(color);
      }

      return { ...item, color, fill: color };
    }) || []
  );
};

export const getAllocationData = (currentAssetAllocation) => {
  let assetsColors = [];

  const getUniqueColor = () => {
    const color = getRandomColor();
    const colorIndex = assetsColors.findIndex(
      (assets) => assets.color === color
    );
    if (colorIndex === -1) {
      return color;
    } else {
      return getUniqueColor();
    }
  };

  const current_asset_allocation = currentAssetAllocation?.map((data) => {
    const color = getUniqueColor();
    assetsColors.push({
      key: data.name,
      color,
    });
    return { ...data, color, fill: color };
  });

  return {
    current_asset_allocation,
    assetsColors: assetsColors,
  };
};

export function formatDaysActive(days) {
  if (!Number.isFinite(days) || days < 0) return "--";

  const years = Math.floor(days / 365);
  const remainingDays = days % 365;

  if (years === 0) return `${days}d`;
  if (remainingDays === 0) return `${years}y`;
  return `${years}y ${remainingDays}d`;
}
